/**
 * Iter 34 — Admin Providers (read-only roster).
 *
 * Per-provider columns the admin actually needs at a glance. Drill-in
 * navigates to a read-only view of that provider's calendar.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, Heartbeat, Plus, X, Buildings, EnvelopeSimple, UserPlus } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAdminProviders, invalidateAdminProviders, useAdminSchools } from "@/lib/adminQueries";
import { RefreshChip } from "@/lib/formatUpdatedAgo";
import AdminShell from "./AdminShell";
import { MessageProviderButton } from "@/components/messaging/MessageProviderButton";
import CreateProviderModal from "@/components/admin/CreateProviderModal";
import ManageSupervisorsModal from "@/components/admin/ManageSupervisorsModal";

function initials(name) {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

export default function AdminProviders() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("");
  const [campusFilter, setCampusFilter] = useState("all");
  const [pickerForId, setPickerForId] = useState("");
  const [pickedSchool, setPickedSchool] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  // FP3-076A — Create-provider modal state.
  const [createOpen, setCreateOpen] = useState(false);
  // FP3-104 — Manage-supervisors modal state (assistant rows).
  const [manageSupFor, setManageSupFor] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // FP3-029B5 — Schools page "Providers" action links here with
  // ?school=<id> to scope the roster down. Wire it via state so the
  // user can clear the chip.
  const schoolFilter = searchParams.get("school") || "";
  // FP3-053 — Super-admin context selector (sidebar) drives these.
  // Forwarded to the backend so the platform-wide roster narrows to
  // the chosen org / campus instead of being filtered client-side.
  const asOrg = (searchParams.get("as_org") || "").trim();
  const asSchool = (searchParams.get("as_school") || "").trim();
  const isSuperAdmin = user?.permission_role === "super_admin";
  // FP3-076A — Org Admin / admin / super_admin can roster providers.
  const canCreateProvider = ["admin", "org_admin", "super_admin"].includes(
    user?.permission_role || "",
  );

  // FP3-076A — Send (or resend) the activation invite to a rostered
  // provider. The backend writes the invite, flips user.status to
  // "invited", and (in preview) returns a raw token — we just refresh
  // the roster afterwards so the row swaps Rostered → Invited.
  const sendInvite = async (providerId, providerName) => {
    try {
      await api.sendProviderInvite(providerId);
      toast.success(`Invite sent to ${providerName || "provider"}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send invite");
    }
  };

  // FP3-119B — Steady-state cache. Data renders immediately from
  // React Query cache on revisits; refetch only when stale (10 min)
  // or when a mutation invalidates the key. Use `refetch()` for
  // the manual Refresh action; `isFetching` gates the small
  // "Refreshing…" indicator without blanking the table.
  const providersQuery = useAdminProviders({
    ...(asOrg ? { as_org: asOrg } : {}),
    ...(asSchool ? { as_school: asSchool } : {}),
  });
  const rows = providersQuery.data || [];
  const loading = providersQuery.isPending;
  const refetching = providersQuery.isFetching && !providersQuery.isPending;
  const dataUpdatedAt = providersQuery.dataUpdatedAt;

  // Mutations still trigger explicit invalidation, which refetches
  // the query in the background (list stays visible).
  const load = useCallback(() => {
    invalidateAdminProviders();
  }, []);

  // FP3-119B — Cached via React Query so navigating to Providers
  // repeatedly doesn't re-hit /admin/schools. Scoped by asOrg so the
  // super_admin selecting a different org gets a fresh key.
  const allSchoolsQuery = useAdminSchools(
    asOrg ? { organization_id: asOrg } : {},
  );
  const allSchools = allSchoolsQuery.data || [];

  const attachSchool = async (userId, schoolId) => {
    try {
      await api.attachUserSchool(userId, schoolId);
      toast.success("School added to provider");
      setPickerForId("");
      setPickedSchool("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add school");
    }
  };

  const detachSchool = async (userId, schoolId, schoolName) => {
    try {
      await api.detachUserSchool(userId, schoolId);
      toast.success(`Removed ${schoolName}`);
      load();
    } catch (err) {
      // FP3-029B2 — Calm warning when the provider still has active
      // student assignments at this school. The backend surfaces the
      // count + school name in the error detail.
      const msg = err?.response?.data?.detail
        || "Could not remove school";
      toast.warning(msg);
    }
  };

  const campuses = useMemo(() => {
    const s = new Set(rows.map((r) => r.campus).filter(Boolean));
    return ["all", ...Array.from(s).sort()];
  }, [rows]);

  const visible = useMemo(() => {
    return rows.filter((r) => {
      if (campusFilter !== "all" && r.campus !== campusFilter) return false;
      if (schoolFilter && !(r.school_ids || []).includes(schoolFilter)) return false;
      if (typeFilter !== "all") {
        const rt = r.provider_role_type || "primary";
        if (typeFilter === "primary" && rt !== "primary") return false;
        if (typeFilter === "assistant" && rt !== "assistant") return false;
      }
      if (filter && !`${r.name} ${r.role} ${r.email || ""}`.toLowerCase().includes(filter.toLowerCase())) return false;
      return true;
    });
  }, [rows, filter, campusFilter, schoolFilter, typeFilter]);

  // FP3-029B5 — Friendly chip + clear-affordance when arriving via the
  // Schools page "Providers" action.
  const schoolFilterName = useMemo(() => {
    if (!schoolFilter) return "";
    return (allSchools.find((s) => s.id === schoolFilter) || {}).name || "";
  }, [schoolFilter, allSchools]);

  return (
    <AdminShell
      title="Providers"
      subtitle="Roster across the school · click a row for a read-only calendar"
    >
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        {canCreateProvider && (
          <Button
            size="sm"
            data-testid="create-provider-btn"
            onClick={() => setCreateOpen(true)}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white h-8 text-[12px]"
          >
            <UserPlus size={12} weight="bold" className="mr-1" />
            Create provider
          </Button>
        )}
        <Input
          data-testid="provider-search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search by name, role, email…"
          className="max-w-sm h-8 text-[12px]"
        />
        <div className="ml-auto">
          <RefreshChip
            dataUpdatedAt={dataUpdatedAt}
            isFetching={refetching}
            onRefresh={() => providersQuery.refetch()}
            testid="providers-refresh"
          />
        </div>
        {schoolFilter && (
          <span
            data-testid="providers-school-filter-chip"
            className="inline-flex items-center gap-1 text-[11px] text-[#1E3F33] bg-[#E8F0EB] px-2 py-1 rounded-full"
          >
            <Buildings size={11} weight="duotone" />
            School: {schoolFilterName || "Selected"}
            <button
              data-testid="providers-school-filter-clear"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete("school");
                setSearchParams(next, { replace: true });
              }}
              className="ml-1 text-[#1E3F33]/60 hover:text-[#7A3A2C]"
              title="Clear school filter"
            >
              <X size={9} />
            </button>
          </span>
        )}
        <div className="flex items-center gap-1 bg-[#F3F1EC] rounded-lg p-0.5">
          {campuses.map((c) => (
            <button
              key={c}
              data-testid={`campus-filter-${c}`}
              onClick={() => setCampusFilter(c)}
              className={`px-3 py-1 rounded-md text-[11px] font-medium transition-all ${
                campusFilter === c ? "bg-white text-[#3F4756] shadow-sm" : "text-[#6B7280]"
              }`}
            >
              {c === "all" ? "All campuses" : c}
            </button>
          ))}
        </div>
        {/* FP3-076A-1 — Provider type filter. "Primary" tab renamed to
            "Providers" so the segmented control never implies a per-
            student ownership concept. Storage value (`primary`) is
            unchanged; only the visible label moved. */}
        <div
          data-testid="provider-type-filter"
          className="flex items-center gap-1 bg-[#F3F1EC] rounded-lg p-0.5"
        >
          {[
            { v: "all", label: "All" },
            { v: "primary", label: "Providers" },
            { v: "assistant", label: "Assistants" },
          ].map((opt) => (
            <button
              key={opt.v}
              data-testid={`provider-type-filter-${opt.v}`}
              onClick={() => setTypeFilter(opt.v)}
              className={`px-3 py-1 rounded-md text-[11px] font-medium transition-all ${
                typeFilter === opt.v
                  ? "bg-white text-[#3F4756] shadow-sm"
                  : "text-[#6B7280]"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}
        </div>
      ) : visible.length === 0 ? (
        <p className="text-[13px] text-[#6B7280] py-12 text-center">No providers match.</p>
      ) : (
        <div className="border border-[#E5E3DB] rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="providers-table">
            <thead className="bg-[#F9F8F6] text-[#6B7280]">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold">Provider</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold">Role · Campus</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold">Caseload</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold">Schools</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold">Active period</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold">Today</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold">Health</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold">Status</th>
                <th className="px-3 py-2"> </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`provider-row-${r.id}`}
                  className="border-t border-[#ECEAE4] hover:bg-[#FAFAF7]"
                >
                  <td className="px-3 py-2.5">
                    <button
                      data-testid={`provider-row-${r.id}-open`}
                      onClick={() => navigate(`/admin/providers/${r.id}`)}
                      className="flex items-center gap-2 text-left hover:opacity-80"
                    >
                      <Avatar className="w-7 h-7 bg-[#E8F0EB]">
                        <AvatarFallback className="text-[10px] font-semibold text-[#1E3F33]">{initials(r.name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="text-[13px] font-medium text-[#1F2937] truncate underline decoration-transparent hover:decoration-[#8FA998]/60 underline-offset-[3px]">
                          {r.name}
                        </p>
                        {r.email && <p className="text-[11px] text-[#6B7280] truncate">{r.email}</p>}
                      </div>
                    </button>
                  </td>
                  <td className="px-3 py-2.5">
                    <p className="text-[12px] text-[#1F2937]">{r.role || "—"}</p>
                    <p className="text-[10px] text-[#6B7280]">{r.campus || "—"}</p>
                    {/* FP3-029B6 — Provider type chip + supervising
                        provider deep-link. */}
                    <div className="flex items-center gap-1 flex-wrap mt-1">
                      {r.provider_role_type === "assistant" ? (
                        <>
                          <span
                            data-testid={`provider-row-${r.id}-type-chip`}
                            className="inline-flex items-center text-[9.5px] uppercase tracking-[0.08em] font-semibold px-1.5 py-0.5 rounded-full bg-[#EEF1F6] text-[#3F4756]"
                          >
                            {r.assistant_type || "Assistant"}
                          </span>
                          {(() => {
                            // FP3-090 — every authorized supervisor is
                            // shown; no supervisor is "primary".
                            const supNames = (r.supervising_providers || [])
                              .map((s) => s.name)
                              .filter(Boolean);
                            const label = supNames.length
                              ? supNames.join(" · ")
                              : r.supervising_provider_name || "";
                            return label ? (
                              <span
                                data-testid={`provider-row-${r.id}-supervising`}
                                className="text-[10px] text-[#6B7280] truncate"
                                title={`Supervised by ${label}`}
                              >
                                ↳ {label}
                              </span>
                            ) : null;
                          })()}
                          {canCreateProvider && (
                            <button
                              type="button"
                              data-testid={`provider-row-${r.id}-manage-supervisors`}
                              onClick={() => setManageSupFor(r)}
                              className="text-[10px] text-[#1E3F33] hover:underline decoration-[#8FA998]/60 underline-offset-[3px]"
                              title="Add or remove this assistant's supervising providers"
                            >
                              Manage supervisors
                            </button>
                          )}
                        </>
                      ) : (
                        // FP3-076A-1 — Was "Primary" — renamed to
                        // "Provider" so this row chip never implies a
                        // global "Primary Provider" identity. Per-student
                        // primary-provider assignment lives on the
                        // student team, not on the user's role flag.
                        <span
                          data-testid={`provider-row-${r.id}-type-chip`}
                          className="inline-flex items-center text-[9.5px] uppercase tracking-[0.08em] font-semibold px-1.5 py-0.5 rounded-full bg-[#E8F0EB] text-[#1E3F33]"
                        >
                          Provider
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    <p className="text-[14px] font-semibold text-[#1F2937]">{r.caseload_count}</p>
                    <p className="text-[10px] text-[#6B7280]">student{r.caseload_count === 1 ? "" : "s"}</p>
                  </td>
                  <td className="px-3 py-2.5">
                    <ProviderSchoolsCell
                      row={r}
                      allSchools={allSchools}
                      pickerOpen={pickerForId === r.id}
                      pickedSchool={pickedSchool}
                      onOpenPicker={() => {
                        setPickerForId(r.id);
                        setPickedSchool("");
                      }}
                      onClosePicker={() => {
                        setPickerForId("");
                        setPickedSchool("");
                      }}
                      onPickSchool={setPickedSchool}
                      onAttach={attachSchool}
                      onDetach={detachSchool}
                      canManage={!!r.user_id}
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    {r.active_period ? (
                      <>
                        <p className="text-[12px] text-[#1F2937]">{r.active_period.name}</p>
                        <p className="text-[10px] text-[#6B7280]">{r.active_period.start_date} → {r.active_period.end_date}</p>
                      </>
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-[#9C6B2E]/30 text-[#9C6B2E]">No active period</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    <p className="text-[12px] text-[#1F2937]">{r.today_sessions.total}</p>
                    <p className="text-[10px] text-[#6B7280]">
                      {r.today_sessions.completed > 0 && `${r.today_sessions.completed}✓ `}
                      {r.today_sessions.cancelled > 0 && `${r.today_sessions.cancelled}⊘`}
                    </p>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {r.health_violations > 0 ? (
                      <Badge className="bg-[#FBEFEC] text-[#B85C5C] hover:bg-[#FBEFEC] text-[10px]" data-testid={`provider-health-${r.id}`}>
                        <Heartbeat size={10} weight="fill" className="mr-0.5" /> {r.health_violations}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-[#1E5A3F]/30 text-[#1E5A3F]">clean</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {/* FP3-076A — Roster status chip + invite affordance.
                        Rostered → "Invite provider" CTA (disabled when
                        no real email yet, with a helper tooltip).
                        Invited → "Pending invite" chip with Resend.
                        Active → existing availability/out indicator. */}
                    {["deactivated", "disabled", "removed"].includes(
                      (r.account_status || "").toLowerCase()
                    ) ? (
                      <Badge
                        data-testid={`provider-status-${r.id}-disabled`}
                        className="bg-[#F8EFEA] text-[#8A2E1E] hover:bg-[#F8EFEA] text-[10px] border border-[#E3C4BE]"
                      >
                        Disabled
                      </Badge>
                    ) : r.provider_status_label === "rostered" ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <Badge
                          data-testid={`provider-status-${r.id}-rostered`}
                          variant="outline"
                          className="text-[10px] border-[#3F4756]/30 text-[#3F4756]"
                        >
                          Rostered
                        </Badge>
                      </div>
                    ) : r.provider_status_label === "invited" ? (
                      <Badge
                        data-testid={`provider-status-${r.id}-invited`}
                        className="bg-[#FDF8F3] text-[#9C6B2E] hover:bg-[#FDF8F3] text-[10px]"
                      >
                        Pending invite
                      </Badge>
                    ) : r.unavailable_today ? (
                      <Badge className="bg-[#FDF8F3] text-[#9C6B2E] hover:bg-[#FDF8F3] text-[10px]">Out · {r.unavailable_today.reason}</Badge>
                    ) : (
                      <span
                        data-testid={`provider-status-${r.id}-active`}
                        className="text-[10px] text-[#9B9890]"
                      >
                        available
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {/* FP3-076A — Invite affordances. Rostered + real
                          email → "Invite". Rostered + no email →
                          disabled with "Add email" helper. Invited →
                          "Resend" reuses the same handler (server
                          409s if a pending invite exists, but we
                          guard at the FE too by checking the chip). */}
                      {r.provider_status_label === "rostered" && (
                        r.has_placeholder_email || !r.email ? (
                          <button
                            type="button"
                            data-testid={`provider-add-email-${r.id}`}
                            onClick={() => navigate(`/admin/providers/${r.id}`)}
                            className="inline-flex items-center gap-1 text-[11px] text-[#9C6B2E] hover:underline"
                            title="Open provider detail page to add an email"
                          >
                            <EnvelopeSimple size={12} /> Add email to invite
                          </button>
                        ) : (
                          <button
                            type="button"
                            data-testid={`provider-invite-${r.id}`}
                            onClick={() => sendInvite(r.id, r.name)}
                            className="inline-flex items-center gap-1 text-[11px] text-[#1E3F33] hover:underline"
                            title="Send activation email"
                          >
                            <EnvelopeSimple size={12} /> Invite provider
                          </button>
                        )
                      )}
                      <MessageProviderButton
                        testid={`provider-message-${r.id}`}
                        size="xs"
                        variant="outline"
                        provider={r}
                        context={{
                          kind: "general",
                          ref_label: "From Providers list",
                        }}
                      />
                      <button
                        data-testid={`provider-view-${r.id}`}
                        onClick={() => navigate(`/admin/providers/${r.id}`)}
                        className="inline-flex items-center gap-1 text-[11px] text-[#3F4756] hover:underline"
                        title="Open provider detail page"
                      >
                        <Eye size={12} /> Detail
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* FP3-076A — Create Provider modal. Primary-provider list
          drives the assistant supervising-provider picker. We only
          surface providers we already know are non-assistant. */}
      <CreateProviderModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => load()}
        schools={allSchools}
        providersForSupervisor={rows
          .filter((r) => (r.provider_role_type || "primary") !== "assistant")
          .map((r) => ({
            id: r.id,
            name: r.name,
            role: r.role || "",
            account_status: r.account_status || "",
            provider_status_label: r.provider_status_label || "",
          }))}
      />
      {/* FP3-104 — Manage supervisors (assistant rows). */}
      <ManageSupervisorsModal
        open={!!manageSupFor}
        assistant={manageSupFor}
        onClose={() => setManageSupFor(null)}
        onSaved={load}
        primaryProviders={rows}
      />
    </AdminShell>
  );
}


function ProviderSchoolsCell({
  row,
  allSchools,
  pickerOpen,
  pickedSchool,
  onOpenPicker,
  onClosePicker,
  onPickSchool,
  onAttach,
  onDetach,
  canManage,
}) {
  const assigned = new Set(row.school_ids || []);
  const available = (allSchools || []).filter((s) => !assigned.has(s.id));
  const isOrgWide = row.access_scope === "organization_wide" || row.access_scope === "platform_wide";
  const submit = async () => {
    if (!pickedSchool) return;
    await onAttach(row.user_id, pickedSchool);
  };
  return (
    <div className="space-y-1">
      {row.access_scope && (
        <span
          data-testid={`provider-scope-${row.id}`}
          className="inline-flex items-center text-[9.5px] uppercase tracking-[0.08em] font-medium px-1.5 py-0.5 rounded-full mr-1"
          style={
            isOrgWide
              ? { backgroundColor: "#E7EDF3", color: "#1F3F4F" }
              : { backgroundColor: "#EEF1F6", color: "#3F4756" }
          }
        >
          {row.access_scope === "platform_wide"
            ? "Platform"
            : row.access_scope === "organization_wide"
            ? "Org-wide"
            : "Assigned"}
        </span>
      )}
      <div className="flex flex-wrap items-center gap-1">
        {(row.school_chips || []).map((s) => (
          <span
            key={s.id}
            data-testid={`provider-school-${row.id}-${s.id}`}
            className="inline-flex items-center gap-0.5 text-[10.5px] px-2 py-0.5 rounded-full bg-[#E8F0EB] text-[#1E3F33]"
          >
            <Buildings size={9} weight="duotone" /> {s.name}
            {canManage && (
              <button
                data-testid={`provider-school-${row.id}-${s.id}-remove`}
                onClick={() => onDetach(row.user_id, s.id, s.name)}
                className="ml-1 text-[#1E3F33]/60 hover:text-[#7A3A2C]"
                title={`Remove ${s.name}`}
              >
                <X size={9} />
              </button>
            )}
          </span>
        ))}
        {(row.school_chips || []).length === 0 && !isOrgWide && (
          <span
            data-testid={`provider-no-schools-${row.id}`}
            className="inline-flex items-center text-[10px] uppercase tracking-[0.08em] font-medium px-1.5 py-0.5 rounded-full"
            style={{ backgroundColor: "#F5EFE3", color: "#7A6A2C" }}
          >
            No schools assigned
          </span>
        )}
        {canManage && available.length > 0 && !pickerOpen && (
          <Button
            data-testid={`provider-add-school-${row.id}`}
            size="sm"
            variant="outline"
            onClick={onOpenPicker}
            className="h-6 text-[10.5px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB] px-1.5 py-0"
          >
            <Plus size={9} className="mr-0.5" /> School
          </Button>
        )}
      </div>
      {canManage && pickerOpen && (
        <div className="flex items-center gap-1 pt-1">
          <select
            data-testid={`provider-school-select-${row.id}`}
            value={pickedSchool}
            onChange={(e) => onPickSchool(e.target.value)}
            className="h-6 border border-[#E5E3DB] rounded-md px-1 text-[10.5px] bg-white min-w-[140px]"
          >
            <option value="">— School —</option>
            {available.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <Button
            data-testid={`provider-school-submit-${row.id}`}
            size="sm"
            onClick={submit}
            disabled={!pickedSchool}
            className="h-6 text-[10.5px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white px-2 py-0"
          >
            Add
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onClosePicker}
            className="h-6 text-[10.5px] border-[#E5E3DB] text-[#6B7280] px-2 py-0"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
